// ConnectedPaintedScene — PaintedScene subclass for multiplayer zone rendering (SMA-64)
//
// Subscribes to ZoneMessageBus and renders:
//   - Remote player sprites with interpolation + perspective scaling
//   - NPCs at server-defined positions
//   - Gather nodes as interactive hotspots
//   - Entity spawns/despawns
//
// All incoming positions are treated as normalised [0,1]² — the coordinator
// must normalise world-space pixel coords before publishing to the bus.

import * as Phaser from 'phaser';
import {
  PaintedScene,
  type PaintedSceneInitData,
} from './PaintedScene';
import {
  ZoneMessageBus,
  type ZoneMessageType,
  type ZoneMessageHandler,
} from './ZoneMessageBus';
import {
  samplePerspectiveScale,
  type PerspectiveSample,
} from './sceneAssetSpec.schema';
import {
  MSG_PLAYER_POSITION,
  MSG_ENTITY_SPAWN,
  MSG_ENTITY_DESPAWN,
  MSG_NPC_SPAWN,
  MSG_NPC_DIALOGUE,
  MSG_GATHER_NODE_SPAWN,
  MSG_GATHER_NODE_COOLDOWN,
  MSG_GATHER_COMPLETE,
  MSG_WEATHER_CHANGE,
  MSG_SKY_STATE,
  MSG_PLAYER_REALM_CHANGE,
  MSG_BUILD_MODE_TOGGLE,
  MSG_BUILD_PLACE,
  MSG_BUILD_REMOVE,
  MSG_BUILD_STATE,
  MSG_BUILD_RESULT,
  MSG_BUILD_BLOCK_PLACED,
  MSG_BUILD_BLOCK_REMOVED,
  MSG_BUILD_CURRENCY_SYNC,
  type BuildBlock,
  type BuildBlockPlacedEvent,
  type BuildBlockRemovedEvent,
  type BuildCurrencySyncEvent,
  type BuildResultEvent,
  type BuildStateEvent,
  type PlayerPositionEvent,
  type EntitySpawnEvent,
  type EntityDespawnEvent,
  type NpcSpawnEvent,
  type NpcDialogueEvent,
  type GatherNodeSpawnEvent,
  type GatherNodeCooldownEvent,
  type GatherCompleteResult,
  type WeatherChangePayload,
  type WeatherState,
  type SkyStatePayload,
  type PlayerRealmChangeEvent,
} from '@game/shared';
import { DimmingStar } from '../../ui/DimmingStar';
import { WeatherSystem } from '../../systems/WeatherSystem';
import { CloudParallaxSystem } from '../../ui/CloudParallaxSystem';
import { RealmSkyTintSystem } from '../../systems/RealmSkyTintSystem';
import { DynamicCamera } from '../../systems/DynamicCamera';
import { DialogueSystem } from '../../ui/DialogueSystem';
import { PlayerHUD } from '../../ui/PlayerHUD';
import { isC5UiEnabled } from '../../ui/c5/c5Flag';
import {
  AutoTravelHUD,
  type AutoTravelRoomLike,
} from '../../ui/AutoTravelHUD';
import { RitualSlotHUD } from '../../ui/RitualSlotHUD';
import { MaterialToast, materialDisplayName } from '../../ui/MaterialToast';
import {
  MSG_AUTO_TRAVEL_STATE,
  MSG_AUTO_TRAVEL_DESTINATIONS,
  type AutoTravelStateEvent,
  type AutoTravelDestinationsEvent,
} from '@game/shared';

// ── Tuning ──────────────────────────────────────────────────────────────────

const INTERP_RATE_PER_SEC = 8;
const POSITION_EMIT_INTERVAL_MS = 100;
const POSITION_EMIT_THRESHOLD = 0.001;
const ENTITY_DEPTH_BASE = 50;
const ENTITY_DEPTH_RANGE = 100;
const LABEL_FONT = "'Almendra', serif";
const LABEL_COLOR = '#eae0c8';
const BUILD_GRID_MIN = 18;
const BUILD_GRID_SIZE = 7;

// ── Types ───────────────────────────────────────────────────────────────────

export interface ConnectedPaintedSceneInitData extends PaintedSceneInitData {
  messageBus?: ZoneMessageBus;
  roomSend?: (type: string, data?: unknown) => void;
}

export const CONNECTED_SCENE_EVENTS = {
  LOCAL_POSITION_UPDATE: 'connected-scene:local-position',
} as const;

export interface LocalPositionPayload {
  zoneId: string;
  localX: number;
  localY: number;
}

/** Ghost rendering: base alpha and sine-wave pulse parameters. */
const GHOST_ALPHA_BASE = 0.3;
const GHOST_ALPHA_AMPLITUDE = 0.1;
const GHOST_ALPHA_SPEED = 0.003;

interface RemotePlayer {
  sessionId: string;
  name: string;
  sprite: Phaser.GameObjects.Sprite;
  shadow: Phaser.GameObjects.Ellipse;
  label: Phaser.GameObjects.Text | null;
  targetNX: number;
  targetNY: number;
  renderNX: number;
  renderNY: number;
  prevRenderNX: number;
  prevRenderNY: number;
  /** True when the player is in ghost attendance state. */
  isGhost: boolean;
}

interface NpcEntity {
  entityId: string;
  npcName: string;
  role: string;
  sprite: Phaser.GameObjects.Sprite;
  shadow: Phaser.GameObjects.Ellipse;
  label: Phaser.GameObjects.Text;
  nx: number;
  ny: number;
}

interface GatherNode {
  nodeId: string;
  nodeType: string;
  sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Ellipse;
  label: Phaser.GameObjects.Text;
  nx: number;
  ny: number;
  onCooldown: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function depthForNY(ny: number): number {
  return ENTITY_DEPTH_BASE + Math.floor(ny * ENTITY_DEPTH_RANGE);
}

// ── Scene ───────────────────────────────────────────────────────────────────

export class ConnectedPaintedScene extends PaintedScene {
  private messageBus: ZoneMessageBus | null = null;
  private busUnsub: (() => void) | null = null;

  private readonly remotePlayers = new Map<string, RemotePlayer>();
  private readonly npcs = new Map<string, NpcEntity>();
  private readonly gatherNodes = new Map<string, GatherNode>();

  private lastPositionEmitAt = 0;
  private prevEmittedNX = -1;
  private prevEmittedNY = -1;

  // ── Migrated game systems (SMA-66) ─────────────────────────────────────
  private dimmingStar: DimmingStar | null = null;
  private weatherSystem: WeatherSystem | null = null;
  private cloudSystem: CloudParallaxSystem | null = null;
  private realmSkyTint: RealmSkyTintSystem | null = null;
  private dynamicCamera: DynamicCamera | null = null;
  private dialogueSystem: DialogueSystem | null = null;
  private playerHUD: PlayerHUD | null = null;
  private autoTravelHUD: AutoTravelHUD | null = null;
  private ritualSlotHUD: RitualSlotHUD | null = null;
  private materialToast: MaterialToast | null = null;
  private roomSend: ((type: string, data?: unknown) => void) | null = null;
  private buildMode = false;
  private buildBalanceSeconds = 0;
  private buildStatus: Phaser.GameObjects.Text | null = null;
  private buildMarkers = new Map<string, Phaser.GameObjects.Rectangle>();

  constructor(phaserSceneKey: string, manifestKey: string) {
    super(phaserSceneKey, manifestKey);
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  init(data?: ConnectedPaintedSceneInitData): void {
    super.init(data);
    this.messageBus = data?.messageBus ?? null;
    this.roomSend = data?.roomSend ?? null;
    this.lastPositionEmitAt = 0;
    this.prevEmittedNX = -1;
    this.prevEmittedNY = -1;
  }

  update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.ready) return;
    this.tickRemotePlayers(delta);
    this.depthSortLocalWalker();
    this.emitLocalPosition();

    // Migrated systems per-frame updates (SMA-66)
    this.weatherSystem?.update(delta);
    this.cloudSystem?.update(delta);
    this.playerHUD?.update();
    this.autoTravelHUD?.update();
    this.ritualSlotHUD?.update();
    this.updateBuildStatus();
    if (this.dynamicCamera) {
      const px = this.walkerNX * this.scale.width;
      const py = this.walkerNY * this.scale.height;
      this.dynamicCamera.update(px, py, delta);
    }
  }

  protected onSceneReady(): void {
    this.subscribeToMessageBus();
    this.bootMigratedSystems();
    this.bootBuildPrototypeUI();
  }

  protected onSceneShutdown(): void {
    this.unsubscribeFromMessageBus();
    this.tearDownBuildPrototypeUI();
    this.tearDownMigratedSystems();
    this.tearDownAll();
  }

  // ── Message bus wiring ──────────────────────────────────────────────────

  private subscribeToMessageBus(): void {
    if (!this.messageBus) return;

    const subs: { type: ZoneMessageType; handler: ZoneMessageHandler<ZoneMessageType> }[] = [
      { type: MSG_PLAYER_POSITION, handler: (p) => this.onPlayerPosition(p as PlayerPositionEvent) },
      { type: MSG_ENTITY_SPAWN, handler: (p) => this.onEntitySpawn(p as EntitySpawnEvent) },
      { type: MSG_ENTITY_DESPAWN, handler: (p) => this.onEntityDespawn(p as EntityDespawnEvent) },
      { type: MSG_NPC_SPAWN, handler: (p) => this.onNpcSpawn(p as NpcSpawnEvent) },
      { type: MSG_NPC_DIALOGUE, handler: (p) => this.onNpcDialogue(p as NpcDialogueEvent) },
      { type: MSG_GATHER_NODE_SPAWN, handler: (p) => this.onGatherNodeSpawn(p as GatherNodeSpawnEvent) },
      { type: MSG_GATHER_NODE_COOLDOWN, handler: (p) => this.onGatherNodeCooldown(p as GatherNodeCooldownEvent) },
      { type: MSG_GATHER_COMPLETE, handler: (p) => this.onGatherComplete(p as GatherCompleteResult) },
      // Migrated system messages (SMA-66)
      { type: MSG_SKY_STATE, handler: (p) => this.onSkyState(p as SkyStatePayload) },
      { type: MSG_WEATHER_CHANGE, handler: (p) => this.onWeatherChange(p as WeatherChangePayload) },
      { type: MSG_PLAYER_REALM_CHANGE, handler: (p) => this.onPlayerRealmChange(p as PlayerRealmChangeEvent) },
      { type: MSG_BUILD_STATE, handler: (p) => this.onBuildState(p as BuildStateEvent) },
      { type: MSG_BUILD_RESULT, handler: (p) => this.onBuildResult(p as BuildResultEvent) },
      { type: MSG_BUILD_BLOCK_PLACED, handler: (p) => this.onBuildBlockPlaced(p as BuildBlockPlacedEvent) },
      { type: MSG_BUILD_BLOCK_REMOVED, handler: (p) => this.onBuildBlockRemoved(p as BuildBlockRemovedEvent) },
      { type: MSG_BUILD_CURRENCY_SYNC, handler: (p) => this.onBuildCurrencySync(p as BuildCurrencySyncEvent) },
    ];

    const unsubs = subs.map(({ type, handler }) =>
      this.messageBus!.subscribe(type, handler as ZoneMessageHandler<typeof type>),
    );
    this.busUnsub = () => { for (const u of unsubs) u(); };
  }

  private unsubscribeFromMessageBus(): void {
    this.busUnsub?.();
    this.busUnsub = null;
  }

  private bootBuildPrototypeUI(): void {
    this.buildStatus = this.add.text(14, 14, '', {
      fontFamily: LABEL_FONT,
      fontSize: '14px',
      color: '#f7e7b4',
      backgroundColor: 'rgba(28, 34, 32, 0.72)',
      padding: { left: 8, right: 8, top: 5, bottom: 5 },
    }).setOrigin(0, 0).setDepth(5000).setVisible(false);

    this.input.keyboard?.on('keydown-B', () => {
      this.roomSend?.(MSG_BUILD_MODE_TOGGLE, { enabled: !this.buildMode });
    });
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.buildMode) return;
      const { tileX, tileY } = this.pointerToBuildTile(pointer);
      if (pointer.rightButtonDown()) {
        this.roomSend?.(MSG_BUILD_REMOVE, { zoneId: this.manifestKey, tileX, tileY });
      } else if (pointer.leftButtonDown()) {
        this.roomSend?.(MSG_BUILD_PLACE, { zoneId: this.manifestKey, tileX, tileY, blockType: 'woven_fence' });
      }
    });
    this.game.canvas?.addEventListener('contextmenu', this.preventContextMenu);
  }

  private tearDownBuildPrototypeUI(): void {
    this.game.canvas?.removeEventListener('contextmenu', this.preventContextMenu);
    this.buildStatus?.destroy();
    this.buildStatus = null;
    for (const marker of this.buildMarkers.values()) marker.destroy();
    this.buildMarkers.clear();
  }

  private preventContextMenu = (event: Event): void => {
    if (this.buildMode) event.preventDefault();
  };

  private onBuildState(ev: BuildStateEvent): void {
    this.buildMode = ev.buildMode;
    this.buildBalanceSeconds = ev.balanceSeconds;
    for (const marker of this.buildMarkers.values()) marker.destroy();
    this.buildMarkers.clear();
    for (const block of ev.blocks) this.renderBuildBlock(block);
  }

  private onBuildResult(ev: BuildResultEvent): void {
    if (typeof ev.balanceSeconds === 'number') this.buildBalanceSeconds = ev.balanceSeconds;
  }

  private onBuildBlockPlaced(ev: BuildBlockPlacedEvent): void {
    this.buildBalanceSeconds = ev.balanceSeconds;
    this.renderBuildBlock(ev.block);
  }

  private onBuildBlockRemoved(ev: BuildBlockRemovedEvent): void {
    this.buildBalanceSeconds = ev.balanceSeconds;
    const marker = this.buildMarkers.get(ev.blockId);
    marker?.destroy();
    this.buildMarkers.delete(ev.blockId);
  }

  private onBuildCurrencySync(ev: BuildCurrencySyncEvent): void {
    this.buildBalanceSeconds = ev.balanceSeconds;
  }

  private renderBuildBlock(block: BuildBlock): void {
    if (block.zoneId !== this.manifestKey) return;
    const existing = this.buildMarkers.get(block.id);
    existing?.destroy();
    const { x, y } = this.buildTileToScreen(block.tileX, block.tileY);
    const marker = this.add.rectangle(x, y, 26, 26, 0x8f6b3f, 0.78)
      .setStrokeStyle(2, 0xf4df9b, 0.9)
      .setDepth(depthForNY(y / Math.max(1, this.scale.height)) + 2);
    this.buildMarkers.set(block.id, marker);
  }

  private pointerToBuildTile(pointer: Phaser.Input.Pointer): { tileX: number; tileY: number } {
    const nx = Phaser.Math.Clamp(pointer.x / Math.max(1, this.scale.width), 0, 0.999);
    const ny = Phaser.Math.Clamp(pointer.y / Math.max(1, this.scale.height), 0, 0.999);
    return {
      tileX: BUILD_GRID_MIN + Math.floor(nx * BUILD_GRID_SIZE),
      tileY: BUILD_GRID_MIN + Math.floor(ny * BUILD_GRID_SIZE),
    };
  }

  private buildTileToScreen(tileX: number, tileY: number): { x: number; y: number } {
    const nx = (tileX - BUILD_GRID_MIN + 0.5) / BUILD_GRID_SIZE;
    const ny = (tileY - BUILD_GRID_MIN + 0.5) / BUILD_GRID_SIZE;
    return {
      x: nx * this.scale.width,
      y: ny * this.scale.height,
    };
  }

  private updateBuildStatus(): void {
    if (!this.buildStatus) return;
    this.buildStatus.setVisible(this.buildMode);
    if (!this.buildMode) return;
    this.buildStatus.setText(`Build ${this.buildBalanceSeconds}s`);
  }

  // ── Remote players ──────────────────────────────────────────────────────

  private onPlayerPosition(ev: PlayerPositionEvent): void {
    const isGhost = ev.attendanceState === 'ghost';
    const existing = this.remotePlayers.get(ev.sessionId);
    if (existing) {
      existing.targetNX = ev.x;
      existing.targetNY = ev.y;
      existing.isGhost = isGhost;
      return;
    }
    this.spawnRemotePlayer(ev.sessionId, ev.sessionId.slice(0, 6), ev.x, ev.y, isGhost);
  }

  private spawnRemotePlayer(sessionId: string, name: string, nx: number, ny: number, isGhost = false): void {
    if (!this.textures.exists('waker-walk-up')) return;

    const w = this.scale.width;
    const h = this.scale.height;
    const px = nx * w;
    const py = ny * h;

    const sprite = this.add.sprite(px, py, 'waker-walk-up', 0);
    sprite.setOrigin(0.5, 1);
    sprite.setDepth(depthForNY(ny));
    if (this.anims.exists('painted-walker-idle')) {
      sprite.play('painted-walker-idle');
    }

    const shadow = this.add.ellipse(px, py, 28, 10, 0x000000, 0.3);
    shadow.setDepth(depthForNY(ny) - 1);

    const label = this.add.text(px, py - 96, name, {
      fontFamily: LABEL_FONT,
      fontSize: '12px',
      color: LABEL_COLOR,
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
    }).setOrigin(0.5, 1).setDepth(depthForNY(ny) + 1);

    this.applyPerspectiveToSprite(sprite, shadow, ny);

    // Apply initial ghost alpha if needed.
    if (isGhost) {
      sprite.setAlpha(GHOST_ALPHA_BASE);
      shadow.setAlpha(GHOST_ALPHA_BASE);
      label.setAlpha(GHOST_ALPHA_BASE);
    }

    this.remotePlayers.set(sessionId, {
      sessionId,
      name,
      sprite,
      shadow,
      label,
      targetNX: nx,
      targetNY: ny,
      renderNX: nx,
      renderNY: ny,
      prevRenderNX: nx,
      prevRenderNY: ny,
      isGhost,
    });
  }

  private tickRemotePlayers(deltaMs: number): void {
    const dt = deltaMs / 1000;
    const t = Math.min(1, INTERP_RATE_PER_SEC * dt);
    const w = this.scale.width;
    const h = this.scale.height;
    const now = this.game.getTime();

    for (const rp of this.remotePlayers.values()) {
      rp.prevRenderNX = rp.renderNX;
      rp.prevRenderNY = rp.renderNY;
      rp.renderNX += (rp.targetNX - rp.renderNX) * t;
      rp.renderNY += (rp.targetNY - rp.renderNY) * t;

      const px = rp.renderNX * w;
      const py = rp.renderNY * h;
      rp.sprite.setPosition(px, py);
      rp.shadow.setPosition(px, py);
      if (rp.label) rp.label.setPosition(px, py - 96);

      const d = depthForNY(rp.renderNY);
      rp.sprite.setDepth(d);
      rp.shadow.setDepth(d - 1);
      if (rp.label) rp.label.setDepth(d + 1);

      this.applyPerspectiveToSprite(rp.sprite, rp.shadow, rp.renderNY);

      // Ghost rendering: sine-wave alpha pulse, forced idle animation.
      if (rp.isGhost) {
        const ghostAlpha = GHOST_ALPHA_BASE + GHOST_ALPHA_AMPLITUDE * Math.sin(now * GHOST_ALPHA_SPEED);
        rp.sprite.setAlpha(ghostAlpha);
        rp.shadow.setAlpha(ghostAlpha);
        if (rp.label) rp.label.setAlpha(ghostAlpha);
        // Ghosts always idle — skip walk animation.
        if (this.anims.exists('painted-walker-idle')
            && rp.sprite.anims.currentAnim?.key !== 'painted-walker-idle') {
          rp.sprite.play('painted-walker-idle');
        }
      } else {
        // Restore full alpha for non-ghost players (in case state changed).
        if (rp.sprite.alpha < 1) {
          rp.sprite.setAlpha(1);
          rp.shadow.setAlpha(1);
          if (rp.label) rp.label.setAlpha(1);
        }
        this.applyRemoteWalkAnim(rp);
      }
    }
  }

  private applyRemoteWalkAnim(rp: RemotePlayer): void {
    const dx = rp.renderNX - rp.prevRenderNX;
    const dy = rp.renderNY - rp.prevRenderNY;
    const moved = Math.hypot(dx, dy) > 0.0004;

    if (!moved) {
      if (this.anims.exists('painted-walker-idle')
          && rp.sprite.anims.currentAnim?.key !== 'painted-walker-idle') {
        rp.sprite.play('painted-walker-idle');
      }
      return;
    }

    let anim: string;
    let flipX = false;
    if (Math.abs(dx) > Math.abs(dy) * 0.6) {
      anim = 'painted-walker-walk-left';
      flipX = dx > 0;
    } else if (dy < 0) {
      anim = 'painted-walker-walk-up';
    } else {
      anim = 'painted-walker-walk-down';
    }
    if (this.anims.exists(anim) && rp.sprite.anims.currentAnim?.key !== anim) {
      rp.sprite.play(anim);
    }
    rp.sprite.flipX = flipX;
  }

  private removeRemotePlayer(sessionId: string): void {
    const rp = this.remotePlayers.get(sessionId);
    if (!rp) return;
    rp.sprite.destroy();
    rp.shadow.destroy();
    if (rp.label) rp.label.destroy();
    this.remotePlayers.delete(sessionId);
  }

  // ── NPCs ────────────────────────────────────────────────────────────────

  private onNpcSpawn(ev: NpcSpawnEvent): void {
    if (this.npcs.has(ev.entityId)) return;
    if (!this.textures.exists('waker-walk-up')) return;

    const w = this.scale.width;
    const h = this.scale.height;
    const px = ev.x * w;
    const py = ev.y * h;

    const sprite = this.add.sprite(px, py, 'waker-walk-up', 0);
    sprite.setOrigin(0.5, 1);
    sprite.setDepth(depthForNY(ev.y));
    if (this.anims.exists('painted-walker-idle')) {
      sprite.play('painted-walker-idle');
    }
    // Slight tint to distinguish NPCs from players
    sprite.setTint(0xd4c4a8);

    const shadow = this.add.ellipse(px, py, 28, 10, 0x000000, 0.3);
    shadow.setDepth(depthForNY(ev.y) - 1);

    const label = this.add.text(px, py - 100, `${ev.npcName}\n${ev.role}`, {
      fontFamily: LABEL_FONT,
      fontSize: '11px',
      color: '#f0d8a0',
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
      align: 'center',
    }).setOrigin(0.5, 1).setDepth(depthForNY(ev.y) + 1);

    this.applyPerspectiveToSprite(sprite, shadow, ev.y);

    this.npcs.set(ev.entityId, {
      entityId: ev.entityId,
      npcName: ev.npcName,
      role: ev.role,
      sprite,
      shadow,
      label,
      nx: ev.x,
      ny: ev.y,
    });
  }

  private removeNpc(entityId: string): void {
    const npc = this.npcs.get(entityId);
    if (!npc) return;
    npc.sprite.destroy();
    npc.shadow.destroy();
    npc.label.destroy();
    this.npcs.delete(entityId);
  }

  // ── Gather nodes ────────────────────────────────────────────────────────

  private onGatherNodeSpawn(ev: GatherNodeSpawnEvent): void {
    if (this.gatherNodes.has(ev.nodeId)) return;

    const w = this.scale.width;
    const h = this.scale.height;
    const px = ev.x * w;
    const py = ev.y * h;

    // Render as a soft glowing ellipse hotspot until per-type art exists
    const sprite = this.add.ellipse(px, py, 32, 24, 0x88cc66, 0.5);
    sprite.setDepth(depthForNY(ev.y));

    const label = this.add.text(px, py - 20, ev.nodeType, {
      fontFamily: LABEL_FONT,
      fontSize: '10px',
      color: '#c8e8a0',
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
    }).setOrigin(0.5, 1).setDepth(depthForNY(ev.y) + 1);

    this.gatherNodes.set(ev.nodeId, {
      nodeId: ev.nodeId,
      nodeType: ev.nodeType,
      sprite,
      label,
      nx: ev.x,
      ny: ev.y,
      onCooldown: false,
    });
  }

  private onGatherNodeCooldown(ev: GatherNodeCooldownEvent): void {
    const node = this.gatherNodes.get(ev.nodeId);
    if (!node) return;
    node.onCooldown = true;

    // Tween node to depleted appearance (30% opacity, grey fill)
    if (node.sprite instanceof Phaser.GameObjects.Ellipse) {
      node.sprite.setFillStyle(0x666666, 0.3);
    }
    this.tweens.add({
      targets: node.label,
      alpha: 0.3,
      duration: 400,
      ease: 'Quad.easeOut',
    });

    // Schedule respawn VFX when cooldown expires
    const delay = ev.availableAt - Date.now();
    if (delay > 0) {
      this.time.delayedCall(delay, () => {
        this.playGatherNodeRespawn(ev.nodeId);
      });
    } else {
      // Already available — restore immediately
      this.playGatherNodeRespawn(ev.nodeId);
    }
  }

  /**
   * Play a gentle sparkle/bloom when a gather node transitions from
   * depleted back to available. 8-12 warm-colored motes fade upward,
   * and the node alpha tweens from 0.3 to 1.0 over 2 seconds.
   */
  private playGatherNodeRespawn(nodeId: string): void {
    const node = this.gatherNodes.get(nodeId);
    if (!node) return;
    node.onCooldown = false;

    // Restore node visual
    if (node.sprite instanceof Phaser.GameObjects.Ellipse) {
      this.tweens.add({
        targets: node.sprite,
        alpha: { from: 0.3, to: 0.5 },
        duration: 2000,
        ease: 'Sine.easeInOut',
        onStart: () => {
          if (node.sprite instanceof Phaser.GameObjects.Ellipse) {
            node.sprite.setFillStyle(0x88cc66, 0.3);
          }
        },
      });
    }

    // Restore label alpha
    this.tweens.add({
      targets: node.label,
      alpha: { from: 0.3, to: 1.0 },
      duration: 2000,
      ease: 'Sine.easeInOut',
    });

    // Spawn warm-colored particle motes
    const w = this.scale.width;
    const h = this.scale.height;
    const cx = node.nx * w;
    const cy = node.ny * h;
    const moteColors = [0xC4825A, 0xDAA520, 0xe8c44a, 0xd4a860];
    const moteCount = 8 + Math.floor(Math.random() * 5); // 8-12

    for (let i = 0; i < moteCount; i++) {
      const angle = (i / moteCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const radius = 8 + Math.random() * 16;
      const mx = cx + Math.cos(angle) * radius;
      const my = cy + Math.sin(angle) * radius;
      const color = moteColors[i % moteColors.length]!;

      const mote = this.add.circle(mx, my, 2 + Math.random() * 1.5, color, 0.7);
      mote.setDepth(depthForNY(node.ny) + 2);

      this.tweens.add({
        targets: mote,
        y: my - 20 - Math.random() * 15,
        x: mx + (Math.random() - 0.5) * 12,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: 1200 + Math.random() * 800,
        ease: 'Quad.easeOut',
        onComplete: () => mote.destroy(),
      });
    }
  }

  private onGatherComplete(ev: GatherCompleteResult): void {
    if (!ev.granted) return;
    const node = this.gatherNodes.get(ev.nodeId);
    if (!node) return;
    // Flash briefly to indicate success
    if (node.sprite instanceof Phaser.GameObjects.Ellipse) {
      node.sprite.setFillStyle(0xffee88, 0.8);
      this.time.delayedCall(300, () => {
        if (node.sprite instanceof Phaser.GameObjects.Ellipse) {
          node.sprite.setFillStyle(0x88cc66, 0.5);
        }
      });
    }

    // Show floating material toasts at the walker's screen position.
    if (this.materialToast && ev.items && ev.items.length > 0) {
      const screenX = this.walkerNX * this.scale.width;
      const screenY = this.walkerNY * this.scale.height;
      for (const item of ev.items) {
        this.materialToast.show(
          item.quantity,
          materialDisplayName(item.itemType),
          screenX,
          screenY,
        );
      }
    }
  }

  private removeGatherNode(nodeId: string): void {
    const node = this.gatherNodes.get(nodeId);
    if (!node) return;
    node.sprite.destroy();
    node.label.destroy();
    this.gatherNodes.delete(nodeId);
  }

  // ── Entity spawn/despawn (generic) ──────────────────────────────────────

  private onEntitySpawn(ev: EntitySpawnEvent): void {
    // Entity spawns for players are handled by MSG_PLAYER_POSITION;
    // this handles non-player entities (enemies, props, etc.) that arrive
    // via the generic entity_spawn channel.
    if (this.remotePlayers.has(ev.entityId)) return;
    if (this.npcs.has(ev.entityId)) return;

    // For now, render generic entities as remote-player-style sprites
    this.spawnRemotePlayer(ev.entityId, ev.name, ev.x, ev.y);
  }

  private onEntityDespawn(ev: EntityDespawnEvent): void {
    this.removeRemotePlayer(ev.entityId);
    this.removeNpc(ev.entityId);
  }

  // ── Depth sorting ───────────────────────────────────────────────────────

  private depthSortLocalWalker(): void {
    if (!this.walker) return;
    const d = depthForNY(this.walkerNY);
    this.walker.setDepth(d);
    if (this.walkerShadow) this.walkerShadow.setDepth(d - 1);
  }

  // ── Perspective helper ──────────────────────────────────────────────────

  private applyPerspectiveToSprite(
    sprite: Phaser.GameObjects.Sprite,
    shadow: Phaser.GameObjects.Ellipse,
    ny: number,
  ): void {
    const profile: ReadonlyArray<PerspectiveSample> | null =
      this.assetSpec?.perspective ?? null;
    if (!profile) return;
    const scale = samplePerspectiveScale(ny, profile);
    sprite.setScale(scale);
    shadow.setScale(scale * 0.9, scale * 0.45);
  }

  // ── Local position relay ────────────────────────────────────────────────

  private emitLocalPosition(): void {
    const now = this.game.getTime();
    if (now - this.lastPositionEmitAt < POSITION_EMIT_INTERVAL_MS) return;

    const nx = this.walkerNX;
    const ny = this.walkerNY;
    if (
      Math.abs(nx - this.prevEmittedNX) < POSITION_EMIT_THRESHOLD &&
      Math.abs(ny - this.prevEmittedNY) < POSITION_EMIT_THRESHOLD
    ) {
      return;
    }

    const payload: LocalPositionPayload = {
      zoneId: this.manifestKey,
      localX: nx,
      localY: ny,
    };
    this.game.events.emit(CONNECTED_SCENE_EVENTS.LOCAL_POSITION_UPDATE, payload);

    this.prevEmittedNX = nx;
    this.prevEmittedNY = ny;
    this.lastPositionEmitAt = now;
  }

  // ── Migrated system lifecycle (SMA-66) ──────────────────────────────────

  private bootMigratedSystems(): void {
    // P1: ambient / environmental
    this.dimmingStar = new DimmingStar(this);
    this.weatherSystem = new WeatherSystem(this);
    this.cloudSystem = new CloudParallaxSystem(this);
    this.cloudSystem.setWindProvider(this.weatherSystem);
    this.cloudSystem.activate();
    this.realmSkyTint = new RealmSkyTintSystem(this);
    this.dynamicCamera = new DynamicCamera(this.cameras.main);

    // P2: UI
    this.dialogueSystem = new DialogueSystem(this);
    // C5 frame renders vitals as ink strokes on the console's paper card, so
    // the camera-fixed Phaser bars stay off the painting entirely.
    this.playerHUD = isC5UiEnabled() ? null : new PlayerHUD(this);
    this.autoTravelHUD = new AutoTravelHUD(this);
    this.wireAutoTravelHUD();
    this.ritualSlotHUD = new RitualSlotHUD(this, this.roomSend);
    this.materialToast = new MaterialToast(this);

    // Wire weather→cloud cascade
    this.events.on(WeatherSystem.EVENTS.CHANGE, (payload: WeatherChangePayload) => {
      if (!payload.isAnnouncement) {
        this.cloudSystem?.applyWeather(payload.current as WeatherState);
      }
    });
  }

  private tearDownMigratedSystems(): void {
    this.dimmingStar?.destroy();
    this.dimmingStar = null;
    this.weatherSystem?.destroy();
    this.weatherSystem = null;
    this.cloudSystem?.destroy();
    this.cloudSystem = null;
    this.realmSkyTint?.destroy();
    this.realmSkyTint = null;
    this.dynamicCamera = null;
    this.dialogueSystem?.destroy();
    this.dialogueSystem = null;
    this.playerHUD?.destroy();
    this.playerHUD = null;
    this.autoTravelHUD?.destroy();
    this.autoTravelHUD = null;
    this.ritualSlotHUD?.destroy();
    this.ritualSlotHUD = null;
    this.materialToast?.destroy();
    this.materialToast = null;
  }

  // ── Migrated system message handlers (SMA-66) ─────────────────────────

  private onSkyState(ev: SkyStatePayload): void {
    this.dimmingStar?.setDimmingPct(ev.dimmingPct);
  }

  private onWeatherChange(ev: WeatherChangePayload): void {
    if (ev.isAnnouncement) {
      this.weatherSystem?.applyWeather(ev.current as WeatherState, true);
    } else {
      this.weatherSystem?.applyWeather(ev.current as WeatherState, false);
    }
  }

  private onPlayerRealmChange(ev: PlayerRealmChangeEvent): void {
    this.realmSkyTint?.setRealm(ev.realmId);
  }

  private wireAutoTravelHUD(): void {
    if (!this.autoTravelHUD || !this.messageBus) return;
    const sendFn = this.roomSend;
    const adapter: AutoTravelRoomLike = {
      onMessage: (type: string, cb: (payload: unknown) => void) => {
        if (type === MSG_AUTO_TRAVEL_STATE) {
          this.messageBus!.subscribe(MSG_AUTO_TRAVEL_STATE, cb as (p: AutoTravelStateEvent) => void);
        } else if (type === MSG_AUTO_TRAVEL_DESTINATIONS) {
          this.messageBus!.subscribe(MSG_AUTO_TRAVEL_DESTINATIONS, cb as (p: AutoTravelDestinationsEvent) => void);
        }
      },
      send: (type: string, data?: unknown) => {
        sendFn?.(type, data);
      },
    };
    this.autoTravelHUD.attach(adapter);
  }

  private onNpcDialogue(ev: NpcDialogueEvent): void {
    if (!this.dialogueSystem) return;
    if (ev.lines && ev.lines.length > 0) {
      this.dialogueSystem.showLines(ev.npcName ?? 'NPC', ev.lines);
    }
  }

  // ── Public API: migrated systems ───────────────────────────────────────

  getDialogueSystem(): DialogueSystem | null {
    return this.dialogueSystem;
  }

  getPlayerHUD(): PlayerHUD | null {
    return this.playerHUD;
  }

  getDynamicCamera(): DynamicCamera | null {
    return this.dynamicCamera;
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  private tearDownAll(): void {
    for (const rp of this.remotePlayers.values()) {
      rp.sprite.destroy();
      rp.shadow.destroy();
      if (rp.label) rp.label.destroy();
    }
    this.remotePlayers.clear();

    for (const npc of this.npcs.values()) {
      npc.sprite.destroy();
      npc.shadow.destroy();
      npc.label.destroy();
    }
    this.npcs.clear();

    for (const node of this.gatherNodes.values()) {
      node.sprite.destroy();
      node.label.destroy();
    }
    this.gatherNodes.clear();
  }

  // ── Public API for coordinator ──────────────────────────────────────────

  setMessageBus(bus: ZoneMessageBus): void {
    this.unsubscribeFromMessageBus();
    this.messageBus = bus;
    if (this.ready) {
      this.subscribeToMessageBus();
    }
  }

  setRoomSend(send: (type: string, data?: unknown) => void): void {
    this.roomSend = send;
  }
}
